import { useNode, useEditor } from "@craftjs/core";
import React, { useState, useRef, useCallback } from "react";
import { ImageUpload } from "../ui/ImageUpload";
import { Box, Typography, Slider, ToggleButton, ToggleButtonGroup } from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import { beforeAfterStyles } from "../../config/styleConfig";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { SliderField } from "../ui/form/SettingsField";

export interface BeforeAfterProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel?: string;
  afterLabel?: string;
  height?: number;
  borderRadius?: number;
  initialPosition?: number;
  aspectRatio?: "landscape" | "portrait" | "square" | "custom";
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
  align?: "left" | "center" | "right";
}

// Use config values for defaults
const DEFAULT_BEFORE_IMAGE = beforeAfterStyles.defaultImages.before;
const DEFAULT_AFTER_IMAGE = beforeAfterStyles.defaultImages.after;

export const BeforeAfter = ({
  beforeImage,
  afterImage,
  beforeLabel = "Before",
  afterLabel = "After",
  height = beforeAfterStyles.height.desktop,
  borderRadius = 12,
  initialPosition = 50,
  aspectRatio = "landscape",
  maxWidth = 0,
  paddingX = 16,
  paddingTop = 16,
  paddingBottom = 16,
  align = "center",
}: BeforeAfterProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { enabled: editorEnabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  // Fallback to defaults if empty string or undefined
  const safeBeforeImage = beforeImage || DEFAULT_BEFORE_IMAGE;
  const safeAfterImage = afterImage || DEFAULT_AFTER_IMAGE;

  // Ensure height is a valid number (handle string values from deserialization)
  const safeHeight =
    typeof height === "number" && height > 0
      ? height
      : typeof height === "string" && parseInt(height) > 0
        ? parseInt(height)
        : beforeAfterStyles.height.desktop;

  // Aspect ratio mapping
  const aspectRatioMap: Record<string, string> = {
    landscape: "16 / 9",
    portrait: "3 / 4",
    square: "1 / 1",
    custom: "auto",
  };
  const cssAspectRatio = aspectRatioMap[aspectRatio] || "auto";

  const [sliderPosition, setSliderPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (editorEnabled) return; // Don't allow dragging in edit mode
    setIsDragging(true);
    handleMove(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (editorEnabled) return;
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  };

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
        }
      }}
      sx={{
        width: "100%",
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
        mx: "auto",
        px: `${paddingX}px`,
        pt: `${paddingTop}px`,
        pb: `${paddingBottom}px`,
        ...(selected && selectedOutlineSx),
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          overflow: "hidden",
          userSelect: "none",
          width: "100%",
          // Alignment using margin
          ml: align === "center" ? "auto" : align === "right" ? "auto" : 0,
          mr: align === "center" ? "auto" : align === "left" ? "auto" : 0,
          minWidth: `${beforeAfterStyles.minWidth}px`,
          // Use aspect-ratio for preset ratios, fixed height for custom
          ...(aspectRatio === "custom"
            ? { height: `${safeHeight}px`, minHeight: `${safeHeight}px` }
            : { aspectRatio: cssAspectRatio, minHeight: "200px" }),
          borderRadius: `${borderRadius}px`,
          flexShrink: 0,
          bgcolor: "#f3f4f6",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
      >
        {/* After Image (Background) */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url('${safeAfterImage}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Before Image (Clipped) */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url('${safeBeforeImage}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
          }}
        />

        {/* Slider Line - using config */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: beforeAfterStyles.slider.lineWidth,
            bgcolor: "white",
            boxShadow: 3,
            cursor: "ew-resize",
            zIndex: 10,
            left: `${sliderPosition}%`,
            transform: "translateX(-50%)",
          }}
        >
          {/* Slider Handle - using config */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: beforeAfterStyles.slider.handleSize,
              height: beforeAfterStyles.slider.handleSize,
              bgcolor: "white",
              borderRadius: "50%",
              boxShadow: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="svg"
              sx={{ width: 20, height: 20, color: "grey.600" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
            </Box>
          </Box>
        </Box>

        {/* Labels - using config */}
        <Box
          sx={{
            position: "absolute",
            top: beforeAfterStyles.label.top,
            left: beforeAfterStyles.label.horizontalOffset,
            px: `${beforeAfterStyles.label.padding.px}px`,
            py: `${beforeAfterStyles.label.padding.py}px`,
            bgcolor: beforeAfterStyles.label.backgroundColor,
            color: "white",
            fontSize: beforeAfterStyles.label.fontSize,
            borderRadius: beforeAfterStyles.label.borderRadius,
          }}
        >
          {beforeLabel}
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: beforeAfterStyles.label.top,
            right: beforeAfterStyles.label.horizontalOffset,
            px: `${beforeAfterStyles.label.padding.px}px`,
            py: `${beforeAfterStyles.label.padding.py}px`,
            bgcolor: beforeAfterStyles.label.backgroundColor,
            color: "white",
            fontSize: beforeAfterStyles.label.fontSize,
            borderRadius: beforeAfterStyles.label.borderRadius,
          }}
        >
          {afterLabel}
        </Box>

        {/* Instructions overlay in editor */}
        {editorEnabled && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(0, 0, 0, 0.2)",
            }}
          >
            <Box
              sx={{
                bgcolor: "white",
                px: 2,
                py: 1,
                borderRadius: 2,
                fontSize: "0.875rem",
                color: "text.secondary",
                boxShadow: 2,
              }}
            >
              Drag slider in preview mode
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Settings Panel
export const BeforeAfterSettings = () => {
  const {
    actions: { setProp },
    beforeImage,
    afterImage,
    beforeLabel,
    afterLabel,
    height,
    borderRadius,
    initialPosition,
    aspectRatio,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
    align,
  } = useNode((node) => ({
    beforeImage: node.data.props.beforeImage,
    afterImage: node.data.props.afterImage,
    beforeLabel: node.data.props.beforeLabel,
    afterLabel: node.data.props.afterLabel,
    height: node.data.props.height,
    borderRadius: node.data.props.borderRadius,
    initialPosition: node.data.props.initialPosition,
    aspectRatio: node.data.props.aspectRatio,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
    align: node.data.props.align,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Before Image */}
      <Box>
        <ImageUpload
          value={beforeImage}
          onChange={(url) => setProp((props: BeforeAfterProps) => (props.beforeImage = url))}
          label="Before Image"
        />
      </Box>

      {/* After Image */}
      <Box>
        <ImageUpload
          value={afterImage}
          onChange={(url) => setProp((props: BeforeAfterProps) => (props.afterImage = url))}
          label="After Image"
        />
      </Box>

      {/* Labels */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            Before Label
          </Typography>
          <input
            type="text"
            value={beforeLabel}
            onChange={(e) => setProp((props: BeforeAfterProps) => (props.beforeLabel = e.target.value))}
            style={{ width: "100%", padding: "4px 8px", border: "1px solid #ddd", borderRadius: 4, fontSize: 13 }}
          />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            After Label
          </Typography>
          <input
            type="text"
            value={afterLabel}
            onChange={(e) => setProp((props: BeforeAfterProps) => (props.afterLabel = e.target.value))}
            style={{ width: "100%", padding: "4px 8px", border: "1px solid #ddd", borderRadius: 4, fontSize: 13 }}
          />
        </Box>
      </Box>

      {/* Aspect Ratio */}
      <Box>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          Aspect Ratio
        </Typography>
        <select
          value={aspectRatio || "landscape"}
          onChange={(e) =>
            setProp(
              (props: BeforeAfterProps) => (props.aspectRatio = e.target.value as BeforeAfterProps["aspectRatio"])
            )
          }
          style={{ width: "100%", padding: "6px 8px", border: "1px solid #ddd", borderRadius: 4, fontSize: 13 }}
        >
          <option value="portrait">Portrait (3:4) - รูปแนวตั้ง</option>
          <option value="square">Square (1:1) - สี่เหลี่ยมจัตุรัส</option>
          <option value="landscape">Landscape (16:9) - รูปแนวนอน</option>
          <option value="custom">Custom Height - กำหนดเอง</option>
        </select>
      </Box>

      {/* Max Width */}
      <SliderField
        label="Max Width"
        value={maxWidth || 0}
        onChange={(v) => setProp((props: BeforeAfterProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      {/* Padding */}
      <SliderField
        label="Padding X"
        value={paddingX ?? 16}
        onChange={(v) => setProp((props: BeforeAfterProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Top"
        value={paddingTop ?? 16}
        onChange={(v) => setProp((props: BeforeAfterProps) => (props.paddingTop = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom ?? 16}
        onChange={(v) => setProp((props: BeforeAfterProps) => (props.paddingBottom = v))}
        min={0}
        max={120}
        unit="px"
      />

      {/* Alignment */}
      <Box>
        <Typography variant="body2" gutterBottom>
          Alignment
        </Typography>
        <ToggleButtonGroup
          value={align || "center"}
          exclusive
          onChange={(_, value) => {
            if (value !== null) {
              setProp((props: BeforeAfterProps) => (props.align = value as BeforeAfterProps["align"]));
            }
          }}
          size="small"
          fullWidth
        >
          <ToggleButton value="left" sx={{ flex: 1 }}>
            <FormatAlignLeftIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="center" sx={{ flex: 1 }}>
            <FormatAlignCenterIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="right" sx={{ flex: 1 }}>
            <FormatAlignRightIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Height - only show when custom */}
      {aspectRatio === "custom" && (
        <Box>
          <Typography variant="body2" gutterBottom>
            Height: {height}px
          </Typography>
          <Slider
            value={height}
            onChange={(_, value) => setProp((props: BeforeAfterProps) => (props.height = value as number))}
            min={200}
            max={600}
            valueLabelDisplay="auto"
            size="small"
          />
        </Box>
      )}

      {/* Border Radius */}
      <Box>
        <Typography variant="body2" gutterBottom>
          Border Radius: {borderRadius}px
        </Typography>
        <Slider
          value={borderRadius}
          onChange={(_, value) => setProp((props: BeforeAfterProps) => (props.borderRadius = value as number))}
          min={0}
          max={32}
          valueLabelDisplay="auto"
          size="small"
        />
      </Box>

      {/* Initial Position */}
      <Box>
        <Typography variant="body2" gutterBottom>
          Initial Position: {initialPosition}%
        </Typography>
        <Slider
          value={initialPosition}
          onChange={(_, value) => setProp((props: BeforeAfterProps) => (props.initialPosition = value as number))}
          min={10}
          max={90}
          valueLabelDisplay="auto"
          size="small"
        />
      </Box>
    </Box>
  );
};

BeforeAfter.craft = {
  displayName: "Before/After",
  props: {
    beforeImage: beforeAfterStyles.defaultImages.before,
    afterImage: beforeAfterStyles.defaultImages.after,
    beforeLabel: "Before",
    afterLabel: "After",
    height: beforeAfterStyles.height.desktop,
    borderRadius: 12,
    initialPosition: 50,
    aspectRatio: "landscape",
    maxWidth: 0,
    paddingX: 16,
    paddingTop: 16,
    paddingBottom: 16,
    align: "center",
  },
  related: {
    settings: BeforeAfterSettings,
  },
};
